import React from 'react';
import PropTypes from 'prop-types';

/**
 * YapeFormField component.
 * Renders a form field with label, input, and error message.
 * @param {Object} props - Component props
 * @param {string} props.id - Input id
 * @param {string} props.name - Input name
 * @param {string} props.type - Input type
 * @param {string} props.label - Field label
 * @param {string} props.value - Input value
 * @param {Function} props.onChange - Change handler
 * @param {Function} props.onBlur - Blur handler
 * @param {string} props.placeholder - Input placeholder
 * @param {string} props.maxLength - Maximum input length
 * @param {boolean} props.hasError - Whether field has error
 * @param {string} props.errorMessage - Error message to display
 * @param {boolean} props.disabled - Whether input is disabled
 * @param {string} props.instructions - Additional instructions text
 */
const YapeFormField = ({
  id,
  name,
  type,
  label,
  value,
  onChange,
  onBlur,
  placeholder,
  maxLength,
  hasError,
  errorMessage,
  disabled,
  instructions,
}) => {
  return (
    <div className="flex flex-col gap-1.5 mb-4 w-full max-w-[400px]">
      <label htmlFor={id} className="block text-base font-semibold text-black mb-0.5">
        {label}
      </label>
      <input
        id={id}
        name={name}
        type={type}
        className={`form-input text-black text-base ${
          hasError ? 'border-error-regular focus:border-error-regular focus:ring-error-regular' : ''
        }`}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        maxLength={maxLength}
        disabled={disabled}
      />
      {hasError && (
        <div className="flex items-center gap-2 text-error-regular text-xs font-medium min-h-[18px] mt-0.5">
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <span>{errorMessage}</span>
        </div>
      )}
      {instructions && (
        <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-slate-500 italic py-px leading-tight min-h-[14px]">
          <span>{instructions}</span>
        </div>
      )}
    </div>
  );
};

YapeFormField.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  maxLength: PropTypes.string.isRequired,
  hasError: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string,
  disabled: PropTypes.bool.isRequired,
  instructions: PropTypes.string,
};

YapeFormField.defaultProps = {
  errorMessage: '',
  instructions: '',
};

export default YapeFormField;
